import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * A wrapper component for protecting routes that require authentication
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The components to render if authenticated
 * @param {string} [props.requiredRole] - Optional role requirement (e.g., 'admin', 'teacher')
 * @param {string} [props.redirectTo='/login'] - Where to redirect if not authenticated
 * @returns {React.ReactElement}
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole,
  redirectTo = '/login'
}) => {
  const { currentUser, userRole, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Show loading state while authentication is being determined
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated || !currentUser) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} />;
  }
  
  // Check role requirements if specified
  if (requiredRole) {
    // Always allow admin to access any role-protected route
    const hasRequiredRole = userRole === requiredRole || userRole === 'admin';
    
    if (!hasRequiredRole) {
      // Redirect to dashboard or unauthorized page
      return <Navigate to="/dashboard" />;
    }
  }
  
  // User is authenticated and has required role, render the protected component
  return children;
};

export default ProtectedRoute; 